import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, CheckCircle2 } from "lucide-react";
import OnboardingProgressBar from "../components/OnboardingProgressBar";
import { DISPLAY_DOMAIN } from "../lib/brand";
// STARSEED-CAPMARKETS-066: draft persistence across the Stripe round-trip +
// human-readable registration error copy (passwords are never stored).
import { loadDraft, saveDraft, clearDraft, registrationErrorMessage } from "../lib/registerDraft";

const RegisterPage = () => {
  // Lazy one-shot read: a returning member (bounced back from the Stripe
  // payment step) gets everything except the password restored.
  const [draft] = useState(loadDraft);
  const [formData, setFormData] = useState(() => ({
    email: draft?.email || "",
    password: "",
    confirmPassword: "",
    full_name: draft?.full_name || "",
    phone: draft?.phone || "",
    role: draft?.role || "client",
    referral_code: draft?.referral_code || "",
    sms_opt_in: Boolean(draft?.sms_opt_in),
    promo_code: draft?.promo_code || "",
  }));
  const draftRestored = Boolean(draft && (draft.email || draft.full_name));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  // STARSEED-CAPMARKETS-059: founding-member offer — while an auto-apply code
  // is active (first N members), it is pre-filled here without the member
  // needing to be handed a code. Field stays editable.
  const [autoApplied, setAutoApplied] = useState(null);
  // Tracks the live promo input so the async auto-apply probe never stamps
  // its banner over a code the member already entered (Copilot review).
  // Seeded from the restored draft so a bounce-back from Stripe keeps the
  // member's own code safe from the probe too (CAPMARKETS-066).
  const promoCodeRef = useRef(draft?.promo_code || "");
  // This page registers via api.post("/auth/start-registration") directly;
  // useAuth is only needed to hydrate the session on the bypass path.
  const { setUserFromToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { code } = useParams();

  useEffect(() => {
    // Get referral code from URL — supports legacy ?ref= and /:code, plus new ?vc=
    const refCode = code || searchParams.get("vc") || searchParams.get("ref");
    if (refCode) {
      const clean = refCode.toUpperCase();
      setFormData(prev => ({ ...prev, referral_code: clean }));
      validateReferralCode(clean);
      // Fire-and-forget click tracking — gives introducer real-time signal
      // that someone landed on their invite link. Silent on failure.
      api.post("/referrals/track-click", { vc: clean }).catch(() => {});
    } else if (draft?.referral_code) {
      // Restored draft (no invite link in the URL): re-validate so the
      // "Introduced by …" confirmation reappears (CAPMARKETS-066). No
      // click tracking — this is the same visit, not a new landing.
      validateReferralCode(draft.referral_code);
    }
    // `validateReferralCode` is a stable in-component fn that only reads
    // network + state setters; including it would re-run this effect on
    // every render. `api` and `setFormData` are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, searchParams]);

  useEffect(() => {
    // Founding-member auto-apply offer (STARSEED-CAPMARKETS-059). Only fills
    // an empty field so a code from the member (or a re-render) never gets
    // clobbered. Silent on failure — registration works without it.
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/promo-codes/auto-apply");
        if (cancelled || !r.data?.active) return;
        // Member already typed / pasted a DIFFERENT code — leave everything
        // alone. A restored draft holding the offer code itself still gets
        // the founding-member banner back (CAPMARKETS-066).
        if (promoCodeRef.current && promoCodeRef.current !== r.data.code) return;
        setAutoApplied(r.data);
        setFormData((prev) =>
          prev.promo_code && prev.promo_code !== r.data.code
            ? prev
            : { ...prev, promo_code: r.data.code }
        );
        setPromoInfo((prev) => prev || {
          valid: true,
          message: `Founding-member offer applied — your $999 join fee is waived (${r.data.remaining} of the free founding spots remain). Then just $99/month.`,
        });
      } catch {
        /* offer probe is best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const validateReferralCode = async (code) => {
    const clean = (code || "").trim().toUpperCase();
    // EchoSense Corporate VC# bypass — founders/inner circle
    if (clean === "EQS-CORP-2026") {
      setReferrerInfo({ referrer_name: "EchoSense Corporate", is_corp: true });
      return;
    }
    try {
      const response = await api.get(`/referrals/validate/${clean}`);
      setReferrerInfo(response.data);
    } catch (error) {
      setReferrerInfo(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === "referral_code" && value.length >= 10) {
      validateReferralCode(value);
    }
    if (name === "promo_code") {
      promoCodeRef.current = value;
      // Clear previous validation when user edits; a manual edit also ends
      // the "auto-applied" presentation — the banner must never describe a
      // code the member replaced.
      setPromoInfo(null);
      setAutoApplied(null);
    }
  };

  const validatePromoCode = async () => {
    const code = (formData.promo_code || "").trim();
    if (!code) {
      setPromoInfo(null);
      return;
    }
    setPromoChecking(true);
    try {
      const r = await api.post("/promo-codes/validate", { code });
      setPromoInfo(r.data);
    } catch {
      setPromoInfo({ valid: false, message: "Could not validate code right now." });
    } finally {
      setPromoChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    // Mandatory: introducer VC# is required to join the Capital Club.
    const vc = (formData.referral_code || "").trim().toUpperCase();
    if (!vc) {
      toast.error("Introducer VC# is required to join the EchoSense Capital Club. Ask your introducer for their VC#, or use the Corporate VC# if invited.");
      return;
    }

    setLoading(true);

    // Persisted BEFORE the Stripe redirect: if the payment step bounces the
    // member back here (decline, bank block, closed tab), everything except
    // the password comes back with them (CAPMARKETS-066).
    saveDraft(formData);

    try {
      // Pay-first flow: open Stripe Checkout for the $99 one-time
      // registration fee. The account itself is created server-side
      // ONLY after Stripe confirms payment (on the /register/success page).
      const response = await api.post("/auth/start-registration", {
        email:         formData.email,
        password:      formData.password,
        full_name:     formData.full_name,
        phone:         formData.phone || undefined,
        role:          formData.role,
        referral_code: vc,
        sms_opt_in:    formData.sms_opt_in,
        promo_code:    formData.promo_code || undefined,
        origin_url:    window.location.origin,
      });
      const { checkout_url, bypass_stripe, access_token, user: newUser } = response.data;

      // STARSEED-CAPMARKETS-067: payment processor not yet configured — the
      // server onboarded the member directly with payment deferred (founding
      // code consumed if provided). Sign them in and continue onboarding.
      if (bypass_stripe && access_token) {
        clearDraft();
        if (typeof setUserFromToken === "function") {
          try { await setUserFromToken(access_token, newUser); }
          catch { localStorage.setItem("token", access_token); }
        } else {
          localStorage.setItem("token", access_token);
        }
        toast.success(
          response.data.is_pioneer
            ? "Welcome, Founding Member — your $999 join fee is waived. Your account is ready."
            : "Welcome! Your account is ready — membership billing will be finalized separately."
        );
        navigate("/command-center");
        return;
      }

      if (!checkout_url) {
        throw new Error("Stripe did not return a checkout URL.");
      }
      // Redirect to Stripe
      window.location.href = checkout_url;
    } catch (error) {
      toast.error(registrationErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-4 mb-8">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-16 h-16 object-contain" />
          <div className="text-center">
            <span className="text-3xl font-semibold text-cream font-serif">EchoSense Capital Club</span>
            <span className="text-platinum-light/70 text-sm block">{DISPLAY_DOMAIN}</span>
          </div>
        </Link>

        {/* 4-step indicator — Create Account is Step 1 of 4 */}
        <div className="mb-6">
          <OnboardingProgressBar currentStep={1} variant="public" />
        </div>

        <Card className="bg-white border-slate-200 shadow-xl animate-fade-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif text-slate-900">Create Account</CardTitle>
            <CardDescription className="text-slate-600">
              Join the EchoSense Capital Club — verification, introducer & relationship portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            {searchParams.get("status") === "cancelled" && (
              <div
                className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4"
                role="alert"
                data-testid="register-card-declined-banner"
              >
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  Payment didn't go through?
                </p>
                <p className="text-sm text-amber-900 leading-relaxed">
                  Some banks block recurring authorizations to financial-services
                  merchants by default. If your card was declined or showed{" "}
                  <em>"does not support this type of purchase,"</em> please call
                  the number on the back of your card and ask them to authorize
                  recurring charges to <strong>EchoSense Capital Club</strong>{" "}
                  (merchant category <strong>6211</strong>), then try again.
                </p>
                <p className="text-xs text-amber-800/80 mt-2">
                  Or use a different personal credit card — corporate, prepaid,
                  and some debit cards may not be enabled for recurring
                  subscriptions.
                </p>
              </div>
            )}
            {draftRestored && (
              <div
                className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                role="status"
                data-testid="register-draft-restored-note"
              >
                <p className="text-sm text-emerald-900">
                  Welcome back — we kept your details. Just re-enter your
                  password to continue.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-slate-700">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700">Phone (optional here — required to complete onboarding)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-700">Account Type</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger className="bg-surface border-platinum/20 text-platinum" data-testid="register-role-select">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  {/* forge-native-select-readability: drop the bg-white override so
                      the dropdown uses the theme-aware SelectContent default
                      (navy+platinum in dark, white+navy in light) — was white+dark
                      text, which the new default text color would invert. */}
                  <SelectContent className="border-platinum/20">
                    <SelectItem value="client">Investor — Verification Portal Member</SelectItem>
                    <SelectItem value="referrer">Introducer — Network Builder</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                      required
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-platinum/60 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="referral_code" className="text-slate-700">
                  Introducer VC# <span className="text-burgundy">*</span>
                </Label>
                <Input
                  id="referral_code"
                  name="referral_code"
                  placeholder="VC-ABC12345  or  EQS-CORP-2026"
                  value={formData.referral_code}
                  onChange={handleChange}
                  required
                  className="bg-white border-slate-200 text-slate-900 uppercase placeholder:text-platinum/60"
                  data-testid="register-referral-input"
                />
                {referrerInfo && referrerInfo.is_corp && (
                  <div className="flex items-center gap-2 text-blue-700 text-sm mt-1" data-testid="register-corp-vc">
                    <CheckCircle2 size={14} />
                    <span>Joining via <strong>EchoSense Corporate</strong> (founders / inner circle).</span>
                  </div>
                )}
                {referrerInfo && !referrerInfo.is_corp && (
                  <div className="flex items-center gap-2 text-emerald text-sm mt-1" data-testid="register-introducer-confirmed">
                    <CheckCircle2 size={14} />
                    <span>Introduced by <strong>{referrerInfo.referrer_name}</strong></span>
                  </div>
                )}
                <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                  An introducer VC# is <strong>required</strong> to join the EchoSense Capital Club.
                  Ask the member who invited you for their personal VC# (they can copy it from their
                  dashboard). If you were invited by EchoSense Corporate directly, use the corporate
                  VC# provided to you.
                </p>
              </div>

              {/* SMS Opt-In — explicit consent for Twilio compliance */}
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="sms-opt-in">
                  <input
                    type="checkbox"
                    checked={formData.sms_opt_in}
                    onChange={(e) => setFormData(prev => ({ ...prev, sms_opt_in: e.target.checked }))}
                    className="mt-1 accent-blue-600"
                  />
                  <span className="text-[12px] text-slate-700 leading-relaxed">
                    I agree to receive transactional SMS messages from EchoSense Capital Club at the phone number
                    above — including KYC and onboarding status updates, appointment reminders, security codes, and
                    compliance notices. Message and data rates may apply. Message frequency varies. Reply STOP to
                    cancel, HELP for help. EchoSense does <strong>not</strong> send marketing SMS or share my number with third parties.
                    See our{" "}
                    <Link to="/sms-terms" target="_blank" className="text-blue-700 hover:underline">SMS Terms</Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" className="text-blue-700 hover:underline">Privacy Policy</Link>.
                  </span>
                </label>
              </div>

              {/* VIP Capital Club Pioneer — promo code (waives the $999 join fee outright) */}
              <div className="rounded border border-amber-300/60 bg-amber-50/60 p-4 space-y-2" data-testid="promo-code-section">
                <div className="flex items-center justify-between">
                  <Label htmlFor="promo_code" className="text-slate-800 font-semibold flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    VIP Capital Club Pioneer Code
                  </Label>
                  <span className="text-[10px] uppercase tracking-widest text-amber-700">
                    {autoApplied ? "Auto-applied" : "Optional"}
                  </span>
                </div>
                {autoApplied ? (
                  <p className="text-[11px] text-slate-600 leading-relaxed" data-testid="promo-auto-applied-blurb">
                    <strong>Founding-member offer:</strong> your Pioneer code has been applied
                    automatically — the <strong>$999 one-time join fee is waived</strong> for the
                    first founding members ({autoApplied.remaining} spots remaining). All members
                    are enrolled in the <strong>$99/month</strong> VIP Capital Club membership.
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    A valid Pioneer code waives the <strong>$999 one-time join fee</strong> — you
                    start with just the monthly membership. New members without a code pay the $999 fee at signup.
                    All members are enrolled in the <strong>$99/month</strong> VIP
                    Capital Club membership. Codes are rotated monthly — request the current code from your introducer.
                  </p>
                )}
                <div className="flex gap-2">
                  <Input
                    id="promo_code"
                    name="promo_code"
                    type="text"
                    value={formData.promo_code}
                    onChange={handleChange}
                    onBlur={validatePromoCode}
                    placeholder="e.g. YACHT101"
                    className="bg-white border-amber-300 text-slate-900 placeholder:text-platinum/60 uppercase tracking-wider"
                    data-testid="promo-code-input"
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validatePromoCode}
                    disabled={promoChecking || !(formData.promo_code || "").trim()}
                    className="border-amber-400 text-amber-800 hover:bg-amber-100"
                    data-testid="promo-code-validate-btn"
                  >
                    {promoChecking ? "…" : "Verify"}
                  </Button>
                </div>
                {promoInfo && promoInfo.valid && (
                  <p className="text-[12px] text-emerald-700 font-medium flex items-center gap-1.5" data-testid="promo-code-valid">
                    <span className="text-emerald-500">✓</span> {promoInfo.message}
                  </p>
                )}
                {promoInfo && !promoInfo.valid && (
                  <p className="text-[12px] text-red-700 flex items-center gap-1.5" data-testid="promo-code-invalid">
                    <span className="text-red-500">✕</span> {promoInfo.message}
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-medium"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading
                  ? "Opening Secure Checkout..."
                  : promoInfo && promoInfo.valid
                    ? "Continue to Secure Checkout · $999 fee waived · $99/mo"
                    : "Continue to Secure Checkout · $999 + $99/mo"}
                {!loading && <ArrowRight className="ml-2" size={18} />}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="text-burgundy hover:text-burgundy-light font-medium">
                  Sign In
                </Link>
              </p>
            </div>

            <p className="text-xs text-slate-500 text-center mt-4 leading-relaxed">
              {promoInfo && promoInfo.valid && promoInfo.waives_join_fee
                ? <>Your Pioneer code waives the $999 fee — membership starts at <strong>$99/month</strong>,{" "}</>
                : <>EchoSense Capital Club membership requires a <strong>$999 one-time registration fee</strong>{" "}</>}
              processed securely by Stripe. Your account is created only after payment is confirmed.
              No funds are held by EchoSense.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
